import React, { useEffect, useState, useCallback, useMemo } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Upload, FileText, Download, Trash2, Check, X, Loader2,
  UserCheck, Send, Clock, ArrowRight, Printer, FileDown, FileJson,
  User, MapPin, Briefcase, Wallet, Folder, StickyNote, Activity, Eye,
} from 'lucide-react';
import { useDropzone } from 'react-dropzone';
import api from '@/utils/api';
import { useAuth } from '@/context/AuthContext';
import { showSuccess, showError } from '@/utils/toast';
import NotesPanel from '@/components/NotesPanel';

const TABS = [
  { id: 'overview', label: 'Overview', icon: Eye },
  { id: 'applicant', label: 'Applicant & KYC', icon: User },
  { id: 'address', label: 'Address', icon: MapPin },
  { id: 'business', label: 'Employment / Business', icon: Briefcase },
  { id: 'financial', label: 'Financial', icon: Wallet },
  { id: 'references', label: 'References', icon: UserCheck },
  { id: 'documents', label: 'Documents', icon: Folder },
  { id: 'notes', label: 'Notes', icon: StickyNote },
  { id: 'timeline', label: 'Activity Timeline', icon: Activity },
];

const DocumentTypes = [
  { value: 'aadhaar', label: 'Aadhaar Card' },
  { value: 'pan', label: 'PAN Card' },
  { value: 'voter_id', label: 'Applicant Voter ID' },
  { value: 'applicant_photo', label: 'Applicant Photo' },
  { value: 'bank_statement', label: 'Bank Statement' },
  { value: 'salary_slip', label: 'Salary Slip' },
  { value: 'employee_id', label: 'Employee ID' },
  { value: 'gst_certificate', label: 'GST Certificate' },
  { value: 'itr', label: 'ITR' },
  { value: 'business_proof', label: 'Business Proof' },
  { value: 'business_photo', label: 'Business Photo' },
  { value: 'residence_photo', label: 'Residence Photo' },
  { value: 'office_photo', label: 'Office Photo' },
  { value: 'property_papers', label: 'Property Papers' },
  { value: 'income_documents', label: 'Income Documents' },
  { value: 'co_applicant_pan', label: 'Co-Applicant PAN Card' },
  { value: 'co_applicant_aadhaar', label: 'Co-Applicant Aadhaar Card' },
  { value: 'co_applicant_voter_id', label: 'Co-Applicant Voter ID' },
  { value: 'co_applicant_photo', label: 'Co-Applicant Photo' },
];

const NO_DATA = (
  <span className="italic text-muted-foreground">No Data Available</span>
);

const formatRole = (r) => (r || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const formatTime = (iso) => (iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const Field = ({ label, value, format }) => {
  const isEmpty = value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
  let display;
  if (isEmpty) {
    display = NO_DATA;
  } else if (format) {
    display = format(value);
  } else {
    display = String(value);
  }
  return (
    <div data-testid={`field-${label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-0.5">{label}</p>
      <p className="text-sm font-medium text-foreground break-words">{display}</p>
    </div>
  );
};

const SectionCard = ({ title, icon: Icon, children, testId }) => (
  <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid={testId}>
    <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
      {Icon && <Icon size={18} />} {title}
    </h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {children}
    </div>
  </div>
);

const assignmentLabel = (type) => {
  if (type === 'submit_to_manager') return 'Submitted to Manager';
  if (type === 'reassign') return 'Reassigned';
  return 'Assigned';
};

const docStatusStyle = (status) => {
  if (status === 'verified') return 'bg-emerald-100 text-emerald-700';
  if (status === 'rejected') return 'bg-rose-100 text-rose-700';
  return 'bg-gray-100 text-gray-700';
};

const LeadDetail = () => {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [lead, setLead] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [timeline, setTimeline] = useState({ activities: [], assigned_history: [] });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedDocType, setSelectedDocType] = useState('');
  const [assignTarget, setAssignTarget] = useState('');
  const [assignRemarks, setAssignRemarks] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [leadRes, docsRes, tlRes, usersRes] = await Promise.all([
        api.get(`/leads/${leadId}`),
        api.get(`/leads/${leadId}/documents`).catch(() => ({ data: [] })),
        api.get(`/leads/${leadId}/timeline`).catch(() => ({ data: { activities: [], assigned_history: [] } })),
        api.get('/users').catch(() => ({ data: [] })),
      ]);
      setLead(leadRes.data);
      setDocuments(docsRes.data);
      setTimeline(tlRes.data);
      setUsers(usersRes.data);
    } catch (e) {
      console.error('Failed to fetch lead:', e);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const userMap = useMemo(() => {
    const m = {};
    (users || []).forEach((u) => { m[u.id] = u; });
    return m;
  }, [users]);

  const userLabel = (uid, embeddedName, embeddedRole) => {
    if (!uid) return NO_DATA;
    const name = embeddedName || userMap[uid]?.full_name;
    if (!name) return NO_DATA;
    const role = (embeddedRole || userMap[uid]?.role || '').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
    return `${name}${role ? ` · ${role}` : ''}`;
  };

  // Assignment eligibility
  const isAssignAuthorized = ['super_admin', 'admin', 'branch_manager', 'team_leader'].includes(user?.role);
  const isDsaLike = ['telecaller', 'sales_executive', 'dsa_partner'].includes(user?.role);
  const isCurrentOwner = lead && (lead.current_owner === user?.id || lead.created_by === user?.id);
  const assignableUsers = (users || []).filter((u) => {
    if (u.id === user?.id) return false;
    if (!u.is_active) return false;
    if (['super_admin', 'admin'].includes(user?.role)) return true;
    if (user?.role === 'branch_manager' || user?.role === 'team_leader') {
      const myBranch = user?.branch_id || user?.branch;
      const theirBranch = u.branch_id || u.branch;
      return !myBranch || !theirBranch || myBranch === theirBranch;
    }
    return false;
  });

  const handleAssign = async () => {
    if (!assignTarget) { showError('Pick a user to assign'); return; }
    setAssigning(true);
    try {
      await api.post(`/leads/${leadId}/assign`, { assigned_to: assignTarget, remarks: assignRemarks || null });
      showSuccess('Lead assigned successfully');
      setAssignTarget(''); setAssignRemarks('');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to assign');
    } finally { setAssigning(false); }
  };

  const handleSubmitToManager = async () => {
    setAssigning(true);
    try {
      await api.post(`/leads/${leadId}/submit-to-manager`, { remarks: assignRemarks || null });
      showSuccess('Lead submitted to your Branch Manager');
      setAssignRemarks('');
      fetchAll();
    } catch (e) {
      showError(e.response?.data?.detail || 'Failed to submit');
    } finally { setAssigning(false); }
  };

  const onDrop = useCallback(async (acceptedFiles) => {
    if (!selectedDocType) { showError('Please select a document type first'); return; }
    setUploading(true);
    for (const file of acceptedFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('document_type', selectedDocType);
        await api.post(`/leads/${leadId}/documents?document_type=${selectedDocType}`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
      } catch (error) {
        showError(`Failed to upload ${file.name}`);
      }
    }
    setUploading(false);
    setSelectedDocType('');
    fetchAll();
  }, [leadId, selectedDocType]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: { 'application/pdf': ['.pdf'], 'image/jpeg': ['.jpg', '.jpeg'], 'image/png': ['.png'] },
  });

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await api.get(`/documents/${documentId}/download`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch { showError('Download failed'); }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) return;
    try {
      await api.delete(`/documents/${documentId}`);
      fetchAll();
    } catch { showError('Failed to delete document'); }
  };

  const handleUpdateStatus = async (documentId, status) => {
    try {
      await api.put(`/documents/${documentId}/status?status=${status}`);
      fetchAll();
    } catch (e) { console.warn("status update failed", e?.message); }
  };

  const downloadBlob = async (urlPath, filename) => {
    setExporting(true);
    try {
      const response = await api.get(urlPath, { responseType: 'blob' });
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      showError(e.response?.data?.detail || 'Export failed');
    } finally { setExporting(false); }
  };

  const handlePrint = () => window.print();

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="animate-spin text-primary" size={40} />
        </div>
      </DashboardLayout>
    );
  }

  if (!lead) {
    return (
      <DashboardLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Lead not found or you don't have access</p>
        </div>
      </DashboardLayout>
    );
  }

  const fmtCurrency = (v) => `₹${Number(v).toLocaleString('en-IN')}`;
  const r1 = lead.reference1 || {};
  const r2 = lead.reference2 || {};

  return (
    <DashboardLayout>
      <div className="print:px-0">
        <button
          onClick={() => navigate('/leads')}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4 transition-colors print:hidden"
          data-testid="back-to-leads"
        >
          <ArrowLeft size={20} /> Back to Leads
        </button>

        {/* Header */}
        <div className="mb-6 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2 flex-wrap">
              <h1 className="text-2xl md:text-3xl font-bold text-foreground">{lead.customer_name}</h1>
              {lead.lead_number && (
                <span className="px-3 py-1 rounded-full bg-primary/10 text-primary text-sm font-mono font-medium">
                  {lead.lead_number}
                </span>
              )}
              <span className="px-3 py-1 rounded-full bg-amber-100 text-amber-700 text-xs uppercase font-medium">
                {(lead.status || 'fresh').replace(/_/g, ' ')}
              </span>
            </div>
            <p className="text-sm text-muted-foreground">
              {lead.loan_type || 'No Product'} · {lead.city || 'No City'} ·
              <span className="ml-1">Created by <span className="font-medium text-foreground">{lead.created_by_name || '—'}</span></span>
            </p>
          </div>
          <div className="flex flex-wrap gap-2 print:hidden">
            <button
              data-testid="view-full-application-button"
              onClick={() => setActiveTab('applicant')}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted flex items-center gap-2"
            >
              <Eye size={16} /> View Full Application
            </button>
            <button
              data-testid="print-lead-button"
              onClick={handlePrint}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted flex items-center gap-2"
            >
              <Printer size={16} /> Print
            </button>
            <button
              data-testid="download-lead-pdf-button"
              onClick={() => downloadBlob(`/leads/${leadId}/application.pdf`, `lead-${lead.lead_number || leadId}.pdf`)}
              disabled={exporting}
              className="px-3 py-2 text-sm bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 flex items-center gap-2 disabled:opacity-50"
            >
              <FileDown size={16} /> Download PDF
            </button>
            <button
              data-testid="export-lead-json-button"
              onClick={() => downloadBlob(`/leads/${leadId}/export.json`, `lead-${lead.lead_number || leadId}.json`)}
              disabled={exporting}
              className="px-3 py-2 text-sm border border-border rounded-lg hover:bg-muted flex items-center gap-2 disabled:opacity-50"
            >
              <FileJson size={16} /> Export Data
            </button>
          </div>
        </div>

        {/* Tab Strip */}
        <div className="mb-6 border-b border-border overflow-x-auto print:hidden" data-testid="lead-detail-tabs">
          <div className="flex gap-1 min-w-max">
            {TABS.map((t) => {
              const Icon = t.icon;
              const active = activeTab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`tab-${t.id}`}
                  onClick={() => setActiveTab(t.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 -mb-px transition-colors ${
                    active
                      ? 'border-primary text-primary'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`}
                >
                  <Icon size={16} /> {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Tab Content */}
        <div className="space-y-6 print:space-y-4 print:!block">
          {/* OVERVIEW */}
          {(activeTab === 'overview' || activeTab === 'print-all') && (
            <>
              <SectionCard title="Lead Overview" icon={Eye} testId="section-overview">
                <Field label="Lead Number" value={lead.lead_number} />
                <Field label="Status" value={lead.status} format={(v) => v.replace(/_/g, ' ').toUpperCase()} />
                <Field label="Product / Loan Type" value={lead.loan_type} />
                <Field label="Loan Amount" value={lead.loan_amount} format={fmtCurrency} />
                <Field label="Lead Source" value={lead.lead_source} />
                <Field label="Created At" value={lead.created_at} format={formatTime} />
              </SectionCard>

              <SectionCard title="Ownership & Assignment" icon={UserCheck} testId="lead-ownership-card">
                <Field label="Created By" value={userLabel(lead.created_by, lead.created_by_name)} />
                <Field label="Current Owner" value={userLabel(lead.current_owner || lead.assigned_to, lead.current_owner_name || lead.assigned_to_name, lead.current_owner_role)} />
                <Field label="Assigned By" value={userLabel(lead.assigned_by, lead.assigned_by_name)} />
                <Field label="Previous Owner" value={userLabel(lead.previous_owner, lead.previous_owner_name)} />
                <Field label="Last Updated By" value={userLabel(lead.last_updated_by, lead.last_updated_by_name)} />
                <Field label="Last Updated At" value={lead.last_updated_at || lead.updated_at} format={formatTime} />
                <Field label="Branch" value={lead.branch_name || lead.branch_id} />
                {lead.assignment_remarks && (
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Field label="Last Assignment Remarks" value={`"${lead.assignment_remarks}"`} />
                  </div>
                )}
              </SectionCard>

              {(isAssignAuthorized || (isDsaLike && isCurrentOwner)) && (
                <div className="bg-card border border-border rounded-xl p-5 md:p-6 print:hidden" data-testid="lead-assign-panel">
                  <h3 className="font-semibold text-foreground mb-3">
                    {isAssignAuthorized ? 'Assign / Reassign Lead' : 'Submit to Branch Manager'}
                  </h3>
                  {isAssignAuthorized && (
                    <select
                      data-testid="assign-user-select"
                      value={assignTarget}
                      onChange={(e) => setAssignTarget(e.target.value)}
                      className="w-full px-3 py-2 mb-3 rounded-lg border border-border bg-background text-sm"
                    >
                      <option value="">Select user to assign…</option>
                      {assignableUsers.map((u) => (
                        <option key={u.id} value={u.id}>
                          {u.full_name} ({formatRole(u.role)})
                        </option>
                      ))}
                    </select>
                  )}
                  <input
                    type="text"
                    data-testid="assign-remarks-input"
                    value={assignRemarks}
                    onChange={(e) => setAssignRemarks(e.target.value)}
                    placeholder="Assignment remarks (optional)"
                    className="w-full px-3 py-2 mb-3 rounded-lg border border-border bg-background text-sm"
                  />
                  <div className="flex gap-2 flex-wrap">
                    {isAssignAuthorized && (
                      <button
                        data-testid="assign-lead-button"
                        onClick={handleAssign}
                        disabled={assigning || !assignTarget}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                      >
                        {assigning ? <Loader2 className="animate-spin" size={16} /> : <UserCheck size={16} />}
                        Assign
                      </button>
                    )}
                    {isDsaLike && isCurrentOwner && (
                      <button
                        data-testid="submit-to-manager-button"
                        onClick={handleSubmitToManager}
                        disabled={assigning}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 disabled:opacity-50"
                      >
                        {assigning ? <Loader2 className="animate-spin" size={16} /> : <Send size={16} />}
                        Submit to Manager
                      </button>
                    )}
                  </div>
                </div>
              )}
            </>
          )}

          {/* APPLICANT & KYC */}
          {activeTab === 'applicant' && (
            <>
              <SectionCard title="Applicant & KYC Details" icon={User} testId="section-applicant">
                <Field label="Applicant Name" value={lead.customer_name} />
                <Field label="Mother's Name" value={lead.mother_name} />
                <Field label="Date of Birth" value={lead.date_of_birth} />
                <Field label="Marital Status" value={lead.marital_status} format={(v) => formatRole(v)} />
                <Field label="Mobile Number" value={lead.mobile_number} />
                <Field label="Email" value={lead.email} />
                <Field label="Alternate Mobile" value={lead.alternate_number} />
                <Field label="Alternate Email" value={lead.alternate_email} />
                <Field label="PAN Number" value={lead.pan_number} />
                <Field label="Aadhaar Number" value={lead.aadhaar_number} />
                <Field label="CIBIL Score" value={lead.cibil_score} />
              </SectionCard>

              {lead.has_co_applicant && (
                <SectionCard title="Co-Applicant Details" icon={User} testId="section-co-applicant">
                  <Field label="Co-Applicant Name" value={lead.co_applicant_name} />
                  <Field label="Co-Applicant Mother's Name" value={lead.co_applicant_mother_name} />
                  <Field label="Mobile Number" value={lead.co_applicant_mobile} />
                  <Field label="Email" value={lead.co_applicant_email} />
                  <Field label="Relationship with Applicant" value={lead.co_applicant_relation} />
                  <div className="sm:col-span-2 lg:col-span-3 text-xs text-muted-foreground">
                    Co-Applicant PAN, Aadhaar, Voter ID &amp; Photo can be uploaded from the <strong>Documents</strong> tab.
                  </div>
                </SectionCard>
              )}
            </>
          )}

          {/* ADDRESS */}
          {activeTab === 'address' && (
            <SectionCard title="Address Details" icon={MapPin} testId="section-address">
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="Current Address" value={lead.current_address} />
              </div>
              <Field label="Current Address Landmark" value={lead.current_address_landmark} />
              <div className="sm:col-span-2 lg:col-span-3">
                <Field label="Permanent Address" value={lead.permanent_address} />
              </div>
              <Field label="Permanent Address Landmark" value={lead.permanent_address_landmark} />
              <Field label="City" value={lead.city} />
              <Field label="State" value={lead.state} />
              <Field label="PIN Code" value={lead.pin_code} />
            </SectionCard>
          )}

          {/* BUSINESS / EMPLOYMENT */}
          {activeTab === 'business' && (
            <>
              <SectionCard title="Employment / Business Details" icon={Briefcase} testId="section-business">
                <Field label="Occupation" value={lead.occupation} />
                <Field label="Company Name" value={lead.company_name} />
                <Field label="Business Name" value={lead.business_name} />
                <Field label="Firm Name" value={lead.firm_name} />
                <Field label="Firm Registration Date" value={lead.firm_registration_date} />
                <div className="sm:col-span-2 lg:col-span-3">
                  <Field label="Firm Address" value={lead.firm_address} />
                </div>
                <Field label="Firm Landmark" value={lead.firm_landmark} />
                <Field label="Business Vintage" value={lead.business_vintage} />
              </SectionCard>

              {lead.loan_type === 'Personal Loan' && (
                <SectionCard title="Personal Loan — Employment Details" icon={Briefcase} testId="section-personal-loan">
                  <Field label="Qualification" value={lead.qualification} />
                  <Field label="Designation" value={lead.designation} />
                  <Field label="Current Company Name" value={lead.current_company_name} />
                  <Field label="Date of Joining" value={lead.date_of_joining} />
                  <Field label="Current Company Experience" value={lead.current_company_work_experience} />
                  <Field label="Total Work Experience" value={lead.total_work_experience} />
                  <div className="sm:col-span-2 lg:col-span-3">
                    <Field label="Office Address" value={lead.office_address} />
                  </div>
                  <Field label="Office Landmark" value={lead.office_landmark} />
                </SectionCard>
              )}
            </>
          )}

          {/* FINANCIAL */}
          {activeTab === 'financial' && (
            <SectionCard title="Financial / Loan Details" icon={Wallet} testId="section-financial">
              <Field label="Product / Loan Type" value={lead.loan_type} />
              <Field label="Loan Amount" value={lead.loan_amount} format={fmtCurrency} />
              <Field label="Monthly Income" value={lead.monthly_income} format={fmtCurrency} />
              <Field label="Existing EMI" value={lead.existing_emi} format={fmtCurrency} />
              <Field label="CIBIL Score" value={lead.cibil_score} />
              <Field label="Business Vintage" value={lead.business_vintage} />
              <Field label="Lead Source" value={lead.lead_source} />
            </SectionCard>
          )}

          {/* REFERENCES */}
          {activeTab === 'references' && (
            <>
              <SectionCard title="Reference 1" icon={UserCheck} testId="section-reference1">
                <Field label="Name" value={r1.name} />
                <Field label="Mobile" value={r1.mobile} />
                <Field label="Relation" value={r1.relation} />
              </SectionCard>
              <SectionCard title="Reference 2" icon={UserCheck} testId="section-reference2">
                <Field label="Name" value={r2.name} />
                <Field label="Mobile" value={r2.mobile} />
                <Field label="Relation" value={r2.relation} />
              </SectionCard>
            </>
          )}

          {/* DOCUMENTS */}
          {activeTab === 'documents' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2 bg-card border border-border rounded-xl p-5 md:p-6 print:hidden" data-testid="section-upload-documents">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Upload size={18} /> Upload Documents
                </h2>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-foreground mb-2">Document Type</label>
                  <select
                    data-testid="document-type-select"
                    value={selectedDocType}
                    onChange={(e) => setSelectedDocType(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-border bg-background text-foreground text-sm"
                  >
                    <option value="">Select document type...</option>
                    {DocumentTypes.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div
                  {...getRootProps()}
                  data-testid="document-dropzone"
                  className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
                    isDragActive ? 'border-primary bg-primary/5' : 'border-border hover:border-primary hover:bg-accent'
                  }`}
                >
                  <input {...getInputProps()} />
                  <Upload className="mx-auto mb-3 text-muted-foreground" size={40} />
                  {(() => {
                    if (uploading) return <p className="text-foreground">Uploading...</p>;
                    if (isDragActive) return <p className="text-foreground">Drop the files here...</p>;
                    return (
                      <>
                        <p className="text-foreground mb-1">Drag &amp; drop files here, or click to select</p>
                        <p className="text-xs text-muted-foreground">Supported: PDF, JPG, PNG</p>
                      </>
                    );
                  })()}
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="section-documents-list">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Folder size={18} /> Documents <span className="text-xs font-normal text-muted-foreground">({documents.length})</span>
                </h2>
                <div className="space-y-3">
                  {documents.length === 0 ? (
                    <p className="text-muted-foreground text-sm text-center py-6">No documents uploaded yet</p>
                  ) : (
                    documents.map((doc) => (
                      <div key={doc.id} data-testid={`document-${doc.id}`} className="border border-border rounded-lg p-3">
                        <div className="flex items-start gap-3">
                          <FileText className="text-primary flex-shrink-0 mt-1" size={18} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-foreground truncate">{doc.original_filename}</p>
                            <p className="text-xs text-muted-foreground capitalize">{(doc.document_type || '').replace('_', ' ')}</p>
                            <span className={`inline-flex mt-1 items-center px-2 py-0.5 rounded text-xs font-medium ${docStatusStyle(doc.status)}`}>{doc.status}</span>
                          </div>
                        </div>
                        <div className="flex items-center gap-1 mt-2 print:hidden">
                          <button onClick={() => handleDownload(doc.id, doc.original_filename)} className="p-1.5 rounded hover:bg-accent" title="Download">
                            <Download size={14} className="text-muted-foreground" />
                          </button>
                          <button onClick={() => handleUpdateStatus(doc.id, 'verified')} className="p-1.5 rounded hover:bg-accent" title="Verify">
                            <Check size={14} className="text-emerald-600" />
                          </button>
                          <button onClick={() => handleUpdateStatus(doc.id, 'rejected')} className="p-1.5 rounded hover:bg-accent" title="Reject">
                            <X size={14} className="text-destructive" />
                          </button>
                          <button onClick={() => handleDeleteDocument(doc.id)} className="p-1.5 rounded hover:bg-destructive/10" title="Delete">
                            <Trash2 size={14} className="text-destructive" />
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          )}

          {/* NOTES */}
          {activeTab === 'notes' && <NotesPanel leadId={leadId} />}

          {/* TIMELINE */}
          {activeTab === 'timeline' && (
            <div className="space-y-6">
              {/* Assignment history */}
              <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="section-assignment-history">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <UserCheck size={18} /> Assignment History
                  <span className="text-xs font-normal text-muted-foreground">({(timeline.assigned_history || []).length})</span>
                </h2>
                {(!timeline.assigned_history || timeline.assigned_history.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No assignments yet</p>
                ) : (
                  <ol className="relative border-l border-border ml-3">
                    {timeline.assigned_history.map((e, i) => (
                      <li key={`${e.timestamp || ''}-${e.assigned_to || ''}-${i}`} className="mb-4 ml-6">
                        <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-amber-500 rounded-full ring-4 ring-background" />
                        <p className="text-sm font-medium text-foreground">
                          {assignmentLabel(e.assignment_type)}
                        </p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          By <span className="text-foreground/80">{e.assigned_by_name || '—'}</span>
                          {e.assigned_by_role && <span> ({formatRole(e.assigned_by_role)})</span>}
                          {' → '}
                          <span className="text-foreground/80">{e.assigned_to_name || '—'}</span>
                          {e.assigned_to_role && <span> ({formatRole(e.assigned_to_role)})</span>}
                          {e.branch_name && <span> · {e.branch_name}</span>}
                        </p>
                        {e.remarks && <p className="mt-1 italic text-xs text-muted-foreground border-l-2 border-border pl-2">"{e.remarks}"</p>}
                        <p className="text-[11px] text-muted-foreground mt-0.5">{formatTime(e.timestamp)}</p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>

              {/* Activity log */}
              <div className="bg-card border border-border rounded-xl p-5 md:p-6" data-testid="section-activity-timeline">
                <h2 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Clock size={18} /> Activity Timeline
                  <span className="text-xs font-normal text-muted-foreground">({(timeline.activities || []).length})</span>
                </h2>
                {(!timeline.activities || timeline.activities.length === 0) ? (
                  <p className="text-sm text-muted-foreground">No activity yet</p>
                ) : (
                  <ol className="relative border-l border-border ml-3">
                    {timeline.activities.map((a) => (
                      <li key={a.id} className="mb-4 ml-6">
                        <span className="absolute -left-1.5 mt-1.5 w-3 h-3 bg-primary rounded-full ring-4 ring-background" />
                        <p className="text-sm font-medium text-foreground">{a.details}</p>
                        {(a.previous_status || a.new_status) && (
                          <div className="mt-1 flex items-center gap-2 text-xs">
                            {a.previous_status && <span className="px-2 py-0.5 rounded-full bg-gray-200 text-gray-700 capitalize">{a.previous_status}</span>}
                            <ArrowRight size={12} className="text-muted-foreground" />
                            {a.new_status && <span className="px-2 py-0.5 rounded-full bg-primary/10 text-primary capitalize">{a.new_status}</span>}
                          </div>
                        )}
                        {a.remarks && <p className="mt-1 italic text-xs text-muted-foreground border-l-2 border-border pl-2">"{a.remarks}"</p>}
                        <p className="mt-1 text-xs text-muted-foreground">
                          <span className="text-foreground/80 font-medium">{a.actor_name || 'System'}</span>
                          {a.actor_role && <span> · {formatRole(a.actor_role)}</span>}
                          {a.actor_employee_id && <span className="ml-1 font-mono">#{a.actor_employee_id}</span>}
                          <span className="ml-2">{formatTime(a.created_at)}</span>
                        </p>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default LeadDetail;
